/**
 * Prescription lines as typed, and what the API's RxLineIn takes (api/app/schemas/v1/prescription.py:
 * RX_MAX_QUANTITY, RX_MAX_DURATION_DAYS, a dose that fits Numeric(8,3)), with each medicine once
 * (D-18). The forms used number inputs with min=0 read through Number(), so a cleared field was sent
 * as 0, 1.5 tablets or -3 days went through, and a medicine could be added twice (DOC-09). Fields
 * are now kept as typed and each problem is named on its line before anything is sent.
 */

import type { RxLineInput } from "../api/doctor";

export const RX_MAX_QUANTITY = 9999;
export const RX_MAX_DURATION_DAYS = 365;
export const RX_MAX_DOSE = "99999.999";
const DOSE_WHOLE_DIGITS = 5;
const DOSE_DECIMALS = 3;

export interface RxLineDraft {
  medicine_id: string;
  medicine_name: string;
  schedule_class: string;
  quantity: string;
  dosage_value: string;
  dosage_unit: string;
  food_relation: string;
  time_of_day: string[];
  /** Blank = no set course. */
  duration_days: string;
}

export const newRxLine = (m: { id: string; name: string; unit_kind: string; schedule_class: string }): RxLineDraft => ({
  medicine_id: m.id,
  medicine_name: m.name,
  schedule_class: m.schedule_class,
  quantity: "1",
  dosage_value: "1",
  dosage_unit: m.unit_kind,
  food_relation: "after_food",
  time_of_day: ["morning"],
  duration_days: "",
});

/** The dose as the API takes it: trimmed, with a decimal comma read as a point ("1,5" → "1.5"). */
export const normaliseDose = (text: string): string => text.trim().replace(",", ".");

export function quantityProblem(text: string): string {
  const value = text.trim();
  if (!value) return "Enter the quantity.";
  const n = Number(value);
  return /^\d+$/.test(value) && n >= 1 && n <= RX_MAX_QUANTITY ? "" : `Enter a whole number from 1 to ${RX_MAX_QUANTITY}.`;
}

export function doseProblem(text: string): string {
  const value = normaliseDose(text);
  if (!value) return "Enter the dose.";
  const parts = /^(\d*)(?:\.(\d*))?$/.exec(value);
  if (!parts || !/\d/.test(value) || Number(value) <= 0) return "Enter a dose above 0, e.g. 1 or 0.5.";
  // As the server counts them: leading zeros add no digits, trailing decimal zeros no places.
  if (parts[1].replace(/^0+/, "").length > DOSE_WHOLE_DIGITS) return `The dose can be at most ${RX_MAX_DOSE}.`;
  if ((parts[2] ?? "").replace(/0+$/, "").length > DOSE_DECIMALS) return "Enter the dose with at most 3 decimal places.";
  return "";
}

export function durationProblem(text: string): string {
  const value = text.trim();
  if (!value) return "";
  const n = Number(value);
  return /^\d+$/.test(value) && n >= 1 && n <= RX_MAX_DURATION_DAYS ? "" : `Enter 1 to ${RX_MAX_DURATION_DAYS} days, or leave it blank.`;
}

export interface RxLineProblems {
  quantity: string;
  dosage_value: string;
  duration_days: string;
  time_of_day: string;
}

export const rxLineProblems = (l: RxLineDraft): RxLineProblems => ({
  quantity: quantityProblem(l.quantity),
  dosage_value: doseProblem(l.dosage_value),
  duration_days: durationProblem(l.duration_days),
  time_of_day: l.time_of_day.length ? "" : "Pick at least one time of day.",
});

/** Every line can be sent, and there is at least one. */
export const rxLinesReady = (lines: RxLineDraft[]): boolean =>
  lines.length > 0 && lines.every((l) => Object.values(rxLineProblems(l)).every((p) => !p));

/** Why this medicine can't be added (it is already on the prescription, D-18), or "". */
export const addProblem = (lines: RxLineDraft[], medicineId: string): string => {
  const existing = lines.find((l) => l.medicine_id === medicineId);
  return existing ? `${existing.medicine_name} is already on this prescription. Change its line instead; a changing dose can go in the notes.` : "";
};

/** The API's unit kinds (the database enum, GET /system/unit-kinds), used until it answers;
 *  creams, gels, lotions and chronic-care forms included (D-06, D-21). */
export const UNIT_KINDS = [
  "tablet", "capsule", "ml", "mg", "drop", "puff", "syrup_ml", "sachet", "strip", "gram",
  "application", "tube", "bottle", "unit", "patch", "spray",
];

// Units counted one by one, so "2 tablets" / "2 applications" but "5 ml" (as the apps show them).
// Anything not listed, including units added later, is shown as sent with underscores as spaces.
const COUNTED_UNITS = new Set([
  "tablet", "capsule", "drop", "puff", "sachet", "strip", "application", "tube", "bottle", "unit", "patch", "spray",
]);
const UNIT_LABELS: Record<string, string> = { gram: "g", syrup_ml: "ml (syrup)" };
const PLURALS: Record<string, string> = { patch: "patches" };

/** A unit as people read it: "g", "ml (syrup)", "application". */
export const unitLabel = (unit: string): string => UNIT_LABELS[unit] ?? unit.replace(/_/g, " ");

/** "1 tablet", "2 tablets", "2.5 ml", "2 patches" — the API sends the dose as "1.000" (DOC-15). */
export function formatDose(value: string | number | null | undefined, unit: string | null | undefined): string {
  const n = Number(typeof value === "string" ? normaliseDose(value) : value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return "—";
  const amount = String(Number(n.toFixed(3))); // "1.000" → "1", "2.500" → "2.5"
  const key = (unit ?? "").trim().toLowerCase();
  if (!key) return amount;
  if (!COUNTED_UNITS.has(key) || n === 1) return `${amount} ${unitLabel(key)}`;
  return `${amount} ${PLURALS[key] ?? `${unitLabel(key)}s`}`;
}

export const toRxLineInput = (l: RxLineDraft): RxLineInput => ({
  medicine_id: l.medicine_id,
  quantity: Number(l.quantity.trim()),
  dosage_value: normaliseDose(l.dosage_value),
  dosage_unit: l.dosage_unit,
  food_relation: l.food_relation,
  time_of_day: l.time_of_day,
  duration_days: l.duration_days.trim() ? Number(l.duration_days.trim()) : null,
});
